import {
  IAssessessments,
  IAssessessmentsPayload,
  IAssessmentOptions,
  IAssessmentQuestions,
  IAssessmentQuestionsPayload,
  IDeleteAssessmentPayload,
  IDownloadAssessmentPayload,
  ISubmitAssessmentPayload,
  IGenerateCloudAssessmentPayload,
  ICloudAssessment,
  IFetchCloudAssessmentsPayload,
  IDownloadCloudAssessmentPayload,
} from '../../models/AssessmentModels';
import { API_ROUTE } from '../ApiRoute';
import ApiService from '../ApiService';
import ApiServiceBase from '../ApiServiceBase';
import { ServerType, ServiceType } from '../ServiceType.data';

export class AssessmentService {
  private readonly service: ApiServiceBase = new ApiService(
    ServiceType.BuildDeploy,
    ServerType.dcaBackend,
  );

  private readonly dcaBackendService: ApiServiceBase = new ApiService(
    ServiceType.None,
    ServerType.dcaBackend,
  );

  public fetchAssessmentOptions(): Promise<IAssessmentOptions> | IAssessmentOptions {
    return this.service.get({
      route: [API_ROUTE.ASSESSMENTS.BASE, API_ROUTE.ASSESSMENTS.PROVIDERS],
    });
  }

  public fetchAssessmentQuestions(
    payload: IAssessmentQuestionsPayload,
  ): Promise<IAssessmentQuestions> | IAssessmentQuestions {
    return this.service.get({
      route: [API_ROUTE.ASSESSMENTS.BASE, API_ROUTE.ASSESSMENTS.QUESTIONS],
      query: {
        csp: JSON.stringify(payload.cloudServiceProviders),
        capability: JSON.stringify(payload.capabilities),
      },
    });
  }

  public fetchAssessments(
    payload: IAssessessmentsPayload,
  ): Promise<Array<IAssessessments>> | Array<IAssessessments> {
    return this.service.get({
      route: [API_ROUTE.ASSESSMENTS.BASE, API_ROUTE.LIST],
      query: {
        user_id: payload.userId,
        project_id: payload.projectId,
        assessment_type: 'cloud_strategy',
      },
    });
  }

  public downloadAssessment(payload: IDownloadAssessmentPayload): Promise<string> | string {
    return this.service.get({
      route: [API_ROUTE.ASSESSMENTS.BASE, API_ROUTE.ASSESSMENTS.DOWNLOAD],
      query: { assessment_id: payload.assessmentId, export_as_json: true },
    });
  }

  public submitAssessment(payload: ISubmitAssessmentPayload): Promise<string> | string {
    return this.service.post(
      { route: [API_ROUTE.ASSESSMENTS.BASE, API_ROUTE.ASSESSMENTS.SUBMIT] },
      {
        user_id: payload.userId,
        project_id: payload.projectId,
        assessment_type: 'cloud_strategy',
        assessment_name: payload.assessmentName,
        file_name: payload.fileName,
        input_file: payload.inputFile,
        csp: payload.cloudServiceProviders,
        capabilities: payload.capabilities,
      },
    );
  }

  public deleteAssessment(payload: IDeleteAssessmentPayload): Promise<string> | string {
    return this.service.get({
      route: [API_ROUTE.ASSESSMENTS.BASE, API_ROUTE.ASSESSMENTS.DELETE],
      query: { assessment_id: payload.assessmentId },
    });
  }

  public generateAssessment(payload: IGenerateCloudAssessmentPayload): Promise<string> | string {
    const formData = new FormData();
    formData.append('file', payload.file);
    formData.append('userdata', payload.userData);
    return this.dcaBackendService.postMultipart(
      { route: [API_ROUTE.CLOUD_ASSESSMENTS.BASE, API_ROUTE.CLOUD_ASSESSMENTS.GENRATE] },
      [
        { name: 'file', content: payload.file },
        { name: 'userdata', content: payload.userData },
      ],
    );
  }

  public fetchCloudAssessments(
    payload: IFetchCloudAssessmentsPayload,
  ): Promise<Array<ICloudAssessment>> | Array<ICloudAssessment> {
    return this.dcaBackendService.get({
      route: [API_ROUTE.CLOUD_ASSESSMENTS.BASE],
      query: {
        account_id: payload.accountId,
      },
    });
  }

  public downloadCloudAssessmentReport(
    payload: IDownloadCloudAssessmentPayload,
  ): Promise<string> | string {
    return this.dcaBackendService.post(
      {
        route: [
          API_ROUTE.CLOUD_ASSESSMENTS.BASE,
          payload.assessmentId,
          API_ROUTE.CLOUD_ASSESSMENTS.DOWNLOAD,
        ],
      },
      {},
    );
  }
}

const assessmentService = new AssessmentService();
export default assessmentService;
